import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, Eye, RotateCcw, Trash2, Loader2, CheckCircle2, XCircle, ClipboardCheck } from "lucide-react";
import { Paginacao, usePaginas } from "@/components/Paginacao";
import { toast } from "sonner";
import { RefreshButton } from "@/components/RefreshButton";
import { confirmar } from "@/components/ConfirmDialog";

const errMsg = (e) => formatApiError(e.response?.data?.detail);
const fmtDH = (iso) => { const d = new Date(iso); return !iso || isNaN(d) ? "—" : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); };
const STATUS = {
  aprovada: ["Aprovada", "bg-green-50 text-green-700 border-green-200"],
  reprovada: ["Reprovada", "bg-amber-50 text-amber-700 border-amber-200"],
  esgotou: ["Sem tentativas", "bg-red-50 text-red-700 border-red-200"],
  andamento: ["Em andamento", "bg-sky-50 text-sky-700 border-sky-200"],
};
const FILTROS = [["", "Todas"], ["esgotou", "Sem tentativas"], ["reprovada", "Reprovadas"], ["aprovada", "Aprovadas"], ["andamento", "Em andamento"]];

/** Cursos → Provas: consultar as provas finais das alunas, ver cada tentativa e liberar novas tentativas. */
export default function Exams() {
  const [data, setData] = useState(null);
  const [curso, setCurso] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [ver, setVer] = useState(null);       // linha aberta no detalhe

  const load = useCallback(() => api.get("/admin/exams", { params: { course_id: curso, status, q } })
    .then((r) => setData(r.data)).catch((e) => { toast.error(errMsg(e)); setData({ rows: [], total: 0, courses: [] }); }), [curso, status, q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const pag = usePaginas(data?.rows, 25);
  const { setPagina } = pag;
  useEffect(() => { setPagina(1); }, [curso, status, q, setPagina]);

  const liberar = async (r) => {
    if (!(await confirmar(`Liberar novas tentativas para ${r.name || r.email} na prova de ${r.course}?\n\nAs ${r.attempts + r.open} tentativa(s) dela vão para a Lixeira (dá para restaurar) e ela pode fazer a prova de novo, do zero.${r.passed ? "\n\nAtenção: ela está APROVADA. Ao liberar, deixa de constar como aprovada até passar de novo (o certificado já emitido continua valendo)." : ""}`))) return;
    try { await api.post(`/courses/${r.course_id}/exam/results/${r.user_id}/reset`); toast.success("Tentativas liberadas. A aluna já pode refazer a prova."); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Provas</h1>
          <p className="text-slate-500 mt-1">Provas finais das alunas em todos os cursos. Veja cada tentativa (questão por questão) e libere novas tentativas quando a aluna precisar refazer.</p>
        </div>
        <RefreshButton onClick={load} />
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="p-4 flex flex-wrap gap-2 items-center border-b border-slate-100">
          <select value={curso} onChange={(e) => setCurso(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white px-3 text-sm" data-testid="exams-course">
            <option value="">Todos os cursos</option>
            {(data?.courses || []).map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
          <div className="flex gap-1 bg-slate-100 rounded-lg p-1 flex-wrap">
            {FILTROS.map(([k, l]) => (
              <button key={k} onClick={() => setStatus(k)} className={`px-3 py-1.5 rounded-md text-sm font-medium ${status === k ? "bg-white text-[#232D4F] shadow-sm" : "text-slate-500"}`} data-testid={`exams-filter-${k || "todas"}`}>{l}</button>
            ))}
          </div>
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar aluna por nome ou e-mail" className="pl-9 h-10" data-testid="exams-search" />
          </div>
        </div>

        {data === null ? (
          <div className="p-4 space-y-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-12 rounded" />)}</div>
        ) : data.rows.length === 0 ? (
          <div className="p-12 text-center"><ClipboardCheck className="h-10 w-10 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">Nenhuma prova com esse filtro.</p></div>
        ) : (
          <div className="overflow-x-auto iep-scroll">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-slate-50/60">
                <th className="px-4 py-3">Aluna</th><th className="px-3 py-3">Curso</th><th className="px-3 py-3">Tentativas</th>
                <th className="px-3 py-3">Melhor nota</th><th className="px-3 py-3">Situação</th><th className="px-3 py-3 hidden md:table-cell">Última</th><th className="px-4 py-3 text-right">Opções</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {pag.itens.map((r) => { const [l, cls] = STATUS[r.status] || [r.status, ""]; return (
                  <tr key={`${r.user_id}-${r.course_id}`} data-testid="exam-row">
                    <td className="px-4 py-2.5"><div className="font-medium text-slate-800">{r.name || "—"}</div><div className="text-xs text-slate-500">{r.email}</div></td>
                    <td className="px-3 py-2.5 text-slate-600 max-w-[220px]"><span className="line-clamp-2">{r.course}</span></td>
                    <td className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{r.attempts}{r.max_attempts ? ` de ${r.max_attempts}` : ""}{r.open ? <span className="text-xs text-sky-700"> · {r.open} aberta</span> : ""}</td>
                    <td className="px-3 py-2.5 font-semibold text-slate-800">{r.best == null ? "—" : `${r.best}%`}<span className="text-xs font-normal text-slate-400">{r.pass_score ? ` (mín. ${r.pass_score}%)` : ""}</span></td>
                    <td className="px-3 py-2.5"><span className={`text-xs font-semibold px-2 py-1 rounded border ${cls}`}>{l}</span>{r.legacy && <span className="block text-[10px] text-slate-400 mt-1">plataforma antiga</span>}</td>
                    <td className="px-3 py-2.5 text-slate-500 hidden md:table-cell whitespace-nowrap">{fmtDH(r.last_at)}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => setVer(r)} data-testid="exam-view"><Eye className="h-4 w-4 mr-1" /> Ver</Button>
                      <Button size="sm" variant="outline" className="ml-2" onClick={() => liberar(r)} data-testid="exam-reset"><RotateCcw className="h-4 w-4 mr-1" /> Liberar novas tentativas</Button>
                    </td>
                  </tr>); })}
              </tbody>
            </table>
            <Paginacao {...pag} rotulo="provas" className="px-4 py-3 border-t border-slate-100" />
            <p className="px-4 py-3 text-xs text-slate-400 border-t border-slate-100">{data.total} prova(s). "Sem tentativas" = reprovou e usou todas as tentativas permitidas: use "Liberar novas tentativas" para ela refazer.</p>
          </div>
        )}
      </div>

      {ver && <Detalhe linha={ver} onClose={() => setVer(null)} onChanged={load} />}
    </div>
  );
}

function Detalhe({ linha, onClose, onChanged }) {
  const [d, setD] = useState(null);
  const [aberta, setAberta] = useState(null);
  const load = useCallback(() => api.get(`/admin/exams/${linha.user_id}/${linha.course_id}`).then((r) => { setD(r.data); setAberta((a) => a ?? r.data.attempts[0]?.id ?? null); })
    .catch((e) => { toast.error(errMsg(e)); onClose(); }), [linha]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  const excluir = async (t) => {
    if (!(await confirmar(`Excluir esta tentativa (${t.score ?? "—"}%, ${fmtDH(t.finished_at || t.started_at)})?\n\nEla vai para a Lixeira (dá para restaurar) e a aluna ganha essa tentativa de volta.${t.passed ? "\n\nAtenção: esta é uma tentativa APROVADA." : ""}`))) return;
    try { await api.delete(`/admin/exams/attempt/${t.id}`); toast.success("Tentativa excluída."); onChanged(); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">{linha.name || linha.email}</DialogTitle>
          <DialogDescription>{linha.course} · nota mínima {linha.pass_score ?? "—"}%{linha.max_attempts ? ` · até ${linha.max_attempts} tentativas` : ""}</DialogDescription>
        </DialogHeader>
        {!d ? <div className="py-10 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
          : d.attempts.length === 0 ? <p className="py-6 text-sm text-slate-500 text-center">Nenhuma tentativa (já foram liberadas).</p> : (
          <div className="space-y-3">
            {d.attempts.map((t, i) => (
              <div key={t.id} className="rounded-xl border border-slate-200 overflow-hidden">
                <div className="flex flex-wrap items-center gap-3 px-4 py-3 bg-slate-50">
                  <button className="flex-1 min-w-[200px] text-left" onClick={() => setAberta(aberta === t.id ? null : t.id)}>
                    <div className="font-semibold text-slate-800">Tentativa {d.attempts.length - i}
                      <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${!t.finished_at ? "bg-sky-50 text-sky-700" : t.passed ? "bg-green-50 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                        {!t.finished_at ? "Não enviada" : t.passed ? "Aprovada" : "Reprovada"}</span>
                      {t.legacy && <span className="ml-2 text-[10px] text-slate-400">plataforma antiga</span>}</div>
                    <div className="text-xs text-slate-500">{t.finished_at ? `Nota ${t.score}%${t.total ? ` · ${t.right} de ${t.total} certas` : ""} · ${fmtDH(t.finished_at)}` : `Iniciada em ${fmtDH(t.started_at)}`}{t.timed_out ? " · tempo esgotado" : ""}</div>
                  </button>
                  <Button size="sm" variant="ghost" className="text-red-600" onClick={() => excluir(t)} data-testid="exam-attempt-delete"><Trash2 className="h-4 w-4 mr-1" /> Excluir tentativa</Button>
                </div>
                {aberta === t.id && (
                  <div className="p-4 space-y-4">
                    {t.questions.length === 0 ? <p className="text-sm text-slate-500">{t.legacy ? "Prova feita na plataforma antiga: só a nota foi importada, sem as respostas." : "Sem respostas registradas."}</p>
                      : t.questions.map((qq, k) => (
                        <div key={k} className="space-y-1.5">
                          <div className="flex items-start gap-2 text-sm font-medium text-slate-800">
                            {qq.correct ? <CheckCircle2 className="h-4 w-4 text-[#1E9E2A] shrink-0 mt-0.5" /> : <XCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />}
                            <span>{k + 1}. {qq.statement}</span>
                          </div>
                          <ul className="pl-6 space-y-1">
                            {qq.options.map((o, j) => (
                              <li key={j} className={`text-sm rounded-md px-2 py-1 ${o.correct ? "bg-green-50 text-green-800" : o.chosen ? "bg-red-50 text-red-700" : "text-slate-600"}`}>
                                {o.chosen ? "● " : "○ "}{o.text}{o.correct ? " — correta" : ""}{o.chosen && !o.correct ? " — marcada" : ""}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
